// tippi/src/pages/admin/support/SupportCaseDetail.js
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { supportApi } from '../../../firebase/functionsService';
import { COLORS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { STATUS_LABEL, CHANNEL_LABEL } from './SupportCases';

const fmt = (v) => v?.toDate?.().toLocaleString('nb-NO') ?? '';
const ms = (v) => v?.toDate?.().getTime() ?? 0;

export default function SupportCaseDetail() {
  const { caseId } = useParams();
  const isMobile = useIsMobile();
  const [c, setC] = useState(null);
  const [internal, setInternal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [markedRead, setMarkedRead] = useState(false);

  useEffect(() => {
    const unsubs = [
      onSnapshot(doc(db, 'supportCases', caseId), s => setC(s.exists() ? { id: s.id, ...s.data() } : false), () => setC(false)),
      onSnapshot(doc(db, 'supportCaseInternal', caseId), s => setInternal(s.exists() ? s.data() : {}), () => setInternal({})),
      onSnapshot(query(collection(db, 'supportMessages'), where('caseId', '==', caseId)),
        s => setMessages(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => ms(a.at) - ms(b.at))), () => setMessages([])),
    ];
    return () => unsubs.forEach(u => u && u());
  }, [caseId]);

  useEffect(() => {
    if (c && c.unreadForAdmin && !markedRead) {
      setMarkedRead(true);
      supportApi.adminUpdateCase({ caseId, markRead: true }).catch(() => {});
    }
  }, [c, caseId, markedRead]);

  async function run(fn) {
    if (busy) return;
    setBusy(true); setError('');
    try { await fn(); } catch (err) { setError(err?.message ?? 'Noe gikk galt.'); } finally { setBusy(false); }
  }

  const sendReply = () => run(async () => {
    const res = await supportApi.adminReplyToCase({ caseId, text: reply.trim() });
    if (res?.emailSent === false) {
      setError(`Svaret er lagret, men e-posten ble ikke sendt: ${res.emailError ?? 'ukjent feil'}. Send på nytt eller ta kontakt på annen måte.`);
      return;
    }
    setReply('');
  });
  const setStatus = (status) => run(() => supportApi.adminUpdateCase({ caseId, status }));
  const saveNote = () => run(async () => { await supportApi.adminUpdateCase({ caseId, note: note.trim() }); setNote(''); });

  if (c === null) return <div style={{ padding: 24, color: COLORS.textLight }}>Laster…</div>;
  if (c === false) return <div style={{ padding: 24 }}>Fant ikke saken. <Link to="/admin/support">Tilbake til sakene</Link></div>;

  const claude = internal?.claude;
  const ctx = internal?.context ?? {};
  const linkedUid = c.userUid ?? internal?.linkedUserUid ?? null;
  const canReply = c.channel === 'app' || c.channel === 'email';

  return (
    <div style={{ padding: isMobile ? 16 : 24, flex: 1, overflowY: 'auto', maxWidth: 900 }}>
      <Link to="/admin/support" style={{ fontSize: 13, color: COLORS.textMid }}>← Alle saker</Link>
      <h2 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 700 }}>{c.title}</h2>
      <div style={{ fontSize: 13, color: COLORS.textMid, marginBottom: 14 }}>
        {CHANNEL_LABEL[c.channel] ?? c.channel} · {STATUS_LABEL[c.status] ?? c.status}
        {(c.userName || c.contactName) && <> · <span>{c.userName ?? c.contactName}</span></>}
        {c.contactPhone && <> · <span>{c.contactPhone}</span></>}
        {(internal?.userEmail || c.contactEmail) && <> · <span>{internal?.userEmail ?? c.contactEmail}</span></>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {c.status !== 'in_progress' && c.status !== 'resolved' && <button type="button" disabled={busy} onClick={() => setStatus('in_progress')} style={ghostBtn}>Start arbeid</button>}
        {c.status !== 'resolved' && <button type="button" disabled={busy} onClick={() => setStatus('resolved')} style={ghostBtn}>Marker som løst</button>}
        {c.status === 'resolved' && <button type="button" disabled={busy} onClick={() => setStatus('in_progress')} style={ghostBtn}>Åpne igjen</button>}
        {linkedUid && <Link to={`/admin/activity?uid=${linkedUid}`} style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-block' }}>Se aktivitet for brukeren</Link>}
      </div>

      {error && <p role="alert" style={{ color: COLORS.dangerText, fontSize: 13 }}>{error}</p>}

      <h3 style={h3}>Samtale</h3>
      {messages.map(m => (
        <div key={m.id} style={{ background: m.from === 'admin' ? COLORS.successBg : COLORS.surfaceHover, borderRadius: 10, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>{m.from === 'admin' ? 'Tippi support' : (c.channel === 'phone' ? 'Henvendelse (telefon)' : 'Kunde')} · {fmt(m.at)}</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.text}</div>
          {m.via === 'email' && m.from === 'admin' && (
            m.emailStatus === 'failed'
              ? <div style={{ fontSize: 11, color: COLORS.dangerText }}>E-posten ble ikke sendt: {m.emailError}</div>
              : <div style={{ fontSize: 11, color: COLORS.textLight }}>E-post sendt</div>
          )}
        </div>
      ))}

      {canReply ? (
        <div style={{ marginTop: 10 }}>
          <textarea aria-label="Svar til kunden" value={reply} onChange={e => setReply(e.target.value)} rows={4} maxLength={2000} style={textarea} placeholder={c.channel === 'email' ? 'Skriv svar. Det sendes på e-post fra support@tippi.no.' : 'Skriv svar. Kunden får det i appen og som varsel.'} />
          <button type="button" disabled={busy || !reply.trim()} onClick={sendReply} style={{ ...primaryBtn, opacity: busy || !reply.trim() ? 0.5 : 1 }}>Send svar</button>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: COLORS.textMid }}>Ring eller skriv til kunden direkte. Bruk interne notater for å dokumentere oppfølgingen.</p>
      )}

      {claude && (
        <>
          <h3 style={h3}>Claudes vurdering <span style={{ fontWeight: 400, fontSize: 12, color: COLORS.textLight }}>({fmt(claude.updatedAt)}, utkast, ikke sendt)</span></h3>
          <pre style={pre}>{claude.analysis}</pre>
          {claude.draftReply && (
            <>
              <div style={{ fontSize: 12, color: COLORS.textLight, margin: '8px 0 4px' }}>Utkast til svar</div>
              <pre style={pre}>{claude.draftReply}</pre>
              {canReply && <button type="button" onClick={() => setReply(claude.draftReply)} style={ghostBtn}>Bruk utkast</button>}
            </>
          )}
        </>
      )}

      <h3 style={h3}>Kontekst</h3>
      <div style={{ fontSize: 13, color: COLORS.textMid }}>
        {ctx.page && <div>Side: {ctx.page}</div>}
        {ctx.device && <div>Enhet: {ctx.device}</div>}
        {ctx.role && <div>Rolle: {ctx.role}</div>}
      </div>
      {(ctx.recentActivity ?? []).length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: COLORS.textMid }}>
          {ctx.recentActivity.map((a, i) => <li key={i}>{a.type}: {a.summary}{a.at ? ` (${new Date(a.at).toLocaleString('nb-NO')})` : ''}</li>)}
        </ul>
      )}

      <h3 style={h3}>Interne notater</h3>
      {(internal?.notes ?? []).map((n, i) => (
        <div key={i} style={{ fontSize: 13, marginBottom: 6 }}>
          <div>{n.text}</div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>{fmt(n.at)}</div>
        </div>
      ))}
      <textarea aria-label="Internt notat" value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={1000} style={textarea} placeholder="Skjules for kunden" />
      <button type="button" disabled={busy || !note.trim()} onClick={saveNote} style={{ ...ghostBtn, opacity: busy || !note.trim() ? 0.5 : 1 }}>Lagre notat</button>
    </div>
  );
}

const h3 = { fontSize: 14, fontWeight: 700, margin: '22px 0 8px' };
const textarea = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 14, fontFamily: 'inherit', marginBottom: 8 };
const pre = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: COLORS.surfaceHover, borderRadius: 8, padding: 10, fontSize: 13, margin: 0, fontFamily: 'inherit' };
const primaryBtn = { padding: '8px 18px', border: 'none', borderRadius: 6, background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const ghostBtn = { padding: '7px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, background: 'transparent', color: COLORS.textDark, cursor: 'pointer', fontSize: 13 };
